/* eslint-disable no-param-reassign */
import React, { JSX, useCallback, useMemo, useState } from 'react';

import {
    LayoutChangeEvent,
    Platform,
    Pressable,
    View,
    ViewStyle,
} from 'react-native';

import Animated, {
    SharedValue,
    useAnimatedScrollHandler,
    useAnimatedStyle,
} from 'react-native-reanimated';

import HourCell from '../Renderers/HourCellRenderer';
import useEpgDragHandlers from '../hooks/useEpgDragHandlers';
import { EPGAlignment, EPGConfig } from '../types';
import { hoursInMilliseconds } from '../utils';
import getStyles from './styles';

type Props = {
    config: EPGConfig;
    listRef?: React.Ref<Animated.ScrollView>;
    startTime: number;
    xScrollOffset: SharedValue<number>;
    skipXScrollOffsetUpdate: SharedValue<boolean>;
    xNowOffset: SharedValue<number>;
    renderCell: (time: number) => JSX.Element;
    onTouchStart?: () => void;
    renderNowMarker?: (onLayout: (e: LayoutChangeEvent) => void) => JSX.Element;
    onNowMarkerPress?: () => void;
    showNowMarker: boolean;
};

const TimeBar = ({
    config,
    listRef,
    startTime,
    xScrollOffset,
    skipXScrollOffsetUpdate,
    xNowOffset,
    renderCell,
    onTouchStart,
    renderNowMarker,
    onNowMarkerPress,
    showNowMarker,
}: Props) => {
    const [componentWidth, setComponentWidth] = useState(0);
    const [markerWidth, setMarkerWidth] = useState(0);
    const [markerLeftAdjusment, setMarkerLeftAdjusment] = useState(0);

    const dataClass = 'epgTimeBar';

    useEpgDragHandlers({
        dataClass,
        offsetX: xScrollOffset,
        isHorizontal: true,
    });

    const scrollHandler = useAnimatedScrollHandler((e) => {
        if (!skipXScrollOffsetUpdate.value) {
            const { x } = e.contentOffset;
            xScrollOffset.value = x;
        }
    }, []);

    const styles = useMemo(() => getStyles(config), []);

    const markerAnimatedStyle = useAnimatedStyle(() => {
        let translateX =
            xNowOffset.value - xScrollOffset.value + config.channelWidth;
        if (config.stickyMarker) {
            translateX = Math.max(
                translateX,
                markerLeftAdjusment +
                    config.stickyMarkerPadding +
                    config.channelWidth,
            );
            translateX = Math.min(
                translateX,
                componentWidth -
                    (markerWidth - markerLeftAdjusment) -
                    config.stickyMarkerPadding,
            );
        }
        return { transform: [{ translateX }] } as ViewStyle;
    }, [markerLeftAdjusment, markerWidth, componentWidth]);

    const markerStyle = useMemo(
        () => [styles.markerStyle, markerAnimatedStyle],
        [],
    );

    const hours: number[] = useMemo(() => {
        const _hours: number[] = [];
        const endTime = startTime + hoursInMilliseconds(config.hoursCount) - 1;
        for (let ts = startTime; ts <= endTime; ts += hoursInMilliseconds()) {
            _hours.push(ts);
        }
        return _hours;
    }, [startTime]);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
        setComponentWidth(e.nativeEvent.layout.width);
    }, []);

    const onMarkerLayout = useCallback((e: LayoutChangeEvent) => {
        const { width } = e.nativeEvent.layout;
        setMarkerWidth(width);
        switch (config.markerToLineAlignment) {
            case EPGAlignment.start:
                setMarkerLeftAdjusment(width);
                break;

            case EPGAlignment.center:
                setMarkerLeftAdjusment(width / 2);
                break;

            case EPGAlignment.end:
            default:
                setMarkerLeftAdjusment(0);
                break;
        }
    }, []);

    const renderItem = useCallback(
        (item: number) => (
            <HourCell
                key={`item-${item}`}
                config={config}
                time={item}
                renderContent={renderCell}
            />
        ),
        [renderCell],
    );

    const Content = useMemo(() => hours.map(renderItem), [hours, renderItem]);

    const NowMarker = useMemo(() => {
        if (!showNowMarker) {
            return null;
        }

        const style = { marginLeft: -markerLeftAdjusment };
        return (
            <Animated.View style={markerStyle}>
                {Platform.isTV ? (
                    <View style={style}>
                        {renderNowMarker?.(onMarkerLayout)}
                    </View>
                ) : (
                    <Pressable
                        style={style}
                        onPress={() => onNowMarkerPress?.()}
                    >
                        {renderNowMarker?.(onMarkerLayout)}
                    </Pressable>
                )}
            </Animated.View>
        );
    }, [
        showNowMarker,
        renderNowMarker,
        onMarkerLayout,
        onNowMarkerPress,
        markerStyle,
        markerLeftAdjusment,
    ]);

    return (
        <View
            style={styles.container}
            onLayout={onLayout}
            dataSet={{ class: dataClass }}
            accessible={false}
            importantForAccessibility="no-hide-descendants"
            accessibilityElementsHidden
        >
            <Animated.ScrollView
                ref={listRef}
                style={styles.list}
                contentContainerStyle={styles.listContent}
                onTouchStart={onTouchStart}
                onScroll={scrollHandler}
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator={false}
                horizontal
            >
                {Content}
            </Animated.ScrollView>
            {NowMarker}
        </View>
    );
};

export default TimeBar;
